import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import PeopleIcon from '@mui/icons-material/People';
import SpaIcon from '@mui/icons-material/Spa';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import SettingsIcon from '@mui/icons-material/Settings';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CommentIcon from '@mui/icons-material/Comment';
import BarChartIcon from '@mui/icons-material/BarChart';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuthStore } from '../../store/authStore';
import Logo from '../common/Logo';

const drawerWidth = 240;

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const handleMainSite = () => {
    handleMenuClose();
    navigate('/');
  };

  const handleSectionToggle = (id) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const menuItems = [
    { type: 'item', text: 'Панель управления', path: '/admin', icon: <DashboardIcon /> },
    {
      type: 'section',
      id: 'content',
      text: 'Содержимое сайта',
      children: [
        { text: 'Объекты', path: '/admin/properties', icon: <HomeWorkIcon /> },
        { text: 'Услуги', path: '/admin/services', icon: <HomeWorkIcon /> },
        { text: 'Видео', path: '/admin/videos', icon: <VideoLibraryIcon /> },
        { text: 'Отзывы', path: '/admin/testimonials', icon: <RateReviewIcon /> },
        { text: 'Блог', path: '/admin/blog', icon: <ArticleIcon /> },
        { text: 'Полезные советы', path: '/admin/tips', icon: <LightbulbIcon /> },
        { text: 'Комментарии блога', path: '/admin/blog-comments', icon: <CommentIcon /> },
        { text: 'Акции', path: '/admin/promotions', icon: <LocalOfferIcon /> },
      ]
    },
    {
      type: 'section',
      id: 'stats',
      text: 'Статистика сайта',
      children: [
        { text: 'Статистика посещений', path: '/admin/statistics', icon: <BarChartIcon /> },
      ]
    },
    {
      type: 'section',
      id: 'coupons',
      text: 'Купоны',
      children: [
        { text: 'Купоны', path: '/admin/coupons', icon: <CardGiftcardIcon /> },
        { text: 'Сканировать купон', path: '/admin/scan-coupon', icon: <QrCodeScannerIcon /> },
      ]
    },
    {
      type: 'section',
      id: 'advertisements',
      text: 'Рекламные рассылки',
      children: [
        { text: 'Рекламные рассылки', path: '/admin/advertisements', icon: <CampaignIcon /> },
      ]
    },
    {
      type: 'section',
      id: 'users',
      text: 'Пользователи и подписчики',
      children: [
        { text: 'Пользователи', path: '/admin/users', icon: <PeopleIcon /> },
        { text: 'Подписчики', path: '/admin/subscribers', icon: <PeopleAltIcon /> },
        ...(user?.role === 'superadmin'
          ? [{ text: 'Управление администраторами', path: '/admin/admin-management', icon: <AdminPanelSettingsIcon /> }]
          : []),
      ]
    },
    {
      type: 'section',
      id: 'appearance',
      text: 'Оформление',
      children: [
        { text: 'Слайдшоу', path: '/admin/slideshow', icon: <SlideshowIcon /> },
        { text: 'Настройки сайта', path: '/admin/settings', icon: <SettingsIcon /> },
      ]
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#2c3e50', // Темно-синий цвет для панели
          color: 'white',
          boxShadow: 3
        }}
      >
        <Toolbar sx={{ 
          px: { xs: 1, sm: 2 }, // Меньше отступы на мобильных устройствах
          minHeight: { xs: '56px', sm: '64px' } // Меньшая высота на мобильных устройствах
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 } }}
          >
            <MenuIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Logo variant="small" />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ 
                ml: 1, 
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.25rem' }, // Меньший размер шрифта на мобильных устройствах
                display: { xs: 'none', sm: 'block' } // Скрываем на очень маленьких экранах
              }}
            >
              Admin Panel
            </Typography>
          </Stack>
          <IconButton onClick={handleMenuOpen}>
            <Avatar 
              sx={{ 
                bgcolor: theme.palette.primary.main,
                width: 35,
                height: 35
              }}
            >
              {user?.name.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.name} ({user?.role})
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleMainSite}>
              <ListItemIcon>
                <HomeIcon fontSize="small" />
              </ListItemIcon>
              На главный сайт
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={isMobile ? open : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            boxShadow: isMobile ? 3 : 1,
            width: isMobile ? '100%' : drawerWidth, // На мобильных устройствах меню на весь экран
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            {menuItems.map((item) => {
              if (item.type === 'item') {
                return (
                  <ListItem 
                    button 
                    key={item.text} 
                    component={Link} 
                    to={item.path}
                    selected={location.pathname === item.path}
                    onClick={isMobile ? handleDrawerToggle : undefined}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(46, 91, 136, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(46, 91, 136, 0.12)',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(46, 91, 136, 0.04)',
                      },
                      py: isMobile ? 1.5 : 1,
                      fontSize: isMobile ? '1.1rem' : 'inherit'
                    }}
                  >
                    <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItem>
                );
              }

              if (item.type === 'section') {
                const isOpen = !!openSections[item.id];

                return (
                  <Box key={item.id} sx={{ mb: 0.5 }}>
                    <ListItem
                      button
                      onClick={() => handleSectionToggle(item.id)}
                      sx={{
                        py: 1,
                        px: isMobile ? 2 : 2.5,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ letterSpacing: 0.8 }}
                          >
                            {item.text}
                          </Typography>
                        }
                      />
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </ListItem>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <ListItem
                            button
                            key={child.text}
                            component={Link}
                            to={child.path}
                            selected={location.pathname === child.path}
                            onClick={isMobile ? handleDrawerToggle : undefined}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(46, 91, 136, 0.08)',
                                '&:hover': {
                                  backgroundColor: 'rgba(46, 91, 136, 0.12)',
                                }
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(46, 91, 136, 0.04)',
                              },
                              py: isMobile ? 1.5 : 1,
                              pl: isMobile ? 4 : 4,
                              fontSize: isMobile ? '1.1rem' : 'inherit',
                            }}
                          >
                            <ListItemIcon sx={{ color: location.pathname === child.path ? 'primary.main' : 'inherit' }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText primary={child.text} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              return null;
            })}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ 
        flexGrow: 1, 
        p: { xs: 1.5, sm: 2, md: 3 }, // Адаптивные отступы
        maxWidth: '100vw',
        overflowX: 'hidden' // Предотвращаем горизонтальную прокрутку
      }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
